/**
 * SF-4845: every step of a copy in order, with its state, times, how long it took, its counts,
 * its progress so far and, when it failed, the error.
 */
import {
  CheckCircleFilled,
  ClockCircleOutlined,
  CloseCircleFilled,
  LoadingOutlined,
  MinusCircleOutlined,
} from '@ant-design/icons';
import { Alert, Space, Tag, Typography } from 'antd';
import { STEP_STATE_LABELS, type StepState } from '../constants';
import type { EnvCopyStep, FlatValues } from '../api/envCopyClient';
import { durationBetween, flatEntries, formatTime } from '../format';

const { Text } = Typography;

const STEP_ICONS: Record<StepState, React.ReactNode> = {
  pending: <ClockCircleOutlined style={{ color: '#8c8c8c' }} />,
  running: <LoadingOutlined style={{ color: '#1677ff' }} />,
  done: <CheckCircleFilled style={{ color: '#52c41a' }} />,
  failed: <CloseCircleFilled style={{ color: '#ff4d4f' }} />,
  skipped: <MinusCircleOutlined style={{ color: '#bfbfbf' }} />,
};

const Values: React.FC<{ title: string; values: FlatValues | null }> = ({ title, values }) => {
  const entries = flatEntries(values);
  if (entries.length === 0) return null;
  return (
    <div>
      <Text type="secondary">{title}</Text>
      <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
        {entries.map((e) => (
          <li key={e.key}>
            {e.label}: {e.value}
          </li>
        ))}
      </ul>
    </div>
  );
};

const listStyle: React.CSSProperties = { listStyle: 'none', margin: 0, padding: 0 };
const rowStyle: React.CSSProperties = { padding: '12px 0', borderBottom: '1px solid #f0f0f0' };

/** An ordered list named "Copy steps": one item per step, in the runner's order. */
export const EnvCopySteps: React.FC<{ steps: EnvCopyStep[]; now: number }> = ({ steps, now }) => (
  <ol aria-label="Copy steps" style={listStyle}>
    {steps.map((step) => {
      const state = STEP_STATE_LABELS[step.state] ?? { label: step.state, color: 'default' };
      const took = durationBetween(step.started_at, step.ended_at, now);
      return (
        <li key={step.id} style={rowStyle}>
          <Space direction="vertical" size={4} style={{ width: '100%' }}>
            <Space>
              <span aria-hidden>{STEP_ICONS[step.state] ?? STEP_ICONS.pending}</span>
              <Text strong>{step.label}</Text>
              <Tag color={state.color}>{state.label}</Tag>
            </Space>
            {(step.started_at || step.ended_at) && (
              <Text type="secondary">
                {step.started_at && `Started ${formatTime(step.started_at)}`}
                {step.ended_at && `, ended ${formatTime(step.ended_at)}`}
                {took && ` (${step.ended_at ? 'took' : 'running for'} ${took})`}
              </Text>
            )}
            <Values title="Counts" values={step.counts} />
            <Values title="Progress" values={step.progress} />
            {step.error && <Alert type="error" showIcon message={step.error} />}
          </Space>
        </li>
      );
    })}
  </ol>
);
